import { useMemo, useState } from "react";
import useFetchPaginated from "#src/hooks/useFetchPaginated";
import paymentService from "#src/services/payment";
import { Link, useOutletContext } from "react-router-dom";
import { TableV2 } from "#src/commonV2/Table";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { useTranslation } from "react-i18next";
import { SearchboxV2 } from "#src/commonV2/Table/Searchbox";
import { Tooltip } from "@lockerpm/design";
import { ContractTypeValue } from "#src/services/payment/enum";
import { DateTime } from "luxon";
import { CheckCircle, InfoCircle } from "@untitled-ui/icons-react";
import { TAILWIND_COLORS } from "#src/utils/common";
import constants from "#src/config/constants";
import { IPaymentContract } from "#src/services/payment/types";
import { contractStatusItems } from "#src/components/workspace/billing/contract/filterItems";
import { ContractStatusValue } from "#src/components/workspace/billing/contract/enum";
import { CustomBadgeV2ForStatus } from "#src/components/workspace/billing/CustomBadge";
import { FilterDropdownV2 } from "#src/commonV2/Table/Filter/FilterDropdown";

export enum BillingContractSearchParamKey {
  STATUS = "status",
}

export const BillingContractTab = () => {
  const { workspaceId } = useOutletContext<{
    workspaceId: string;
  }>();

  const { t, i18n } = useTranslation("workspace", {
    keyPrefix: "billing.tab.contract",
  });

  const locale = i18n.language;

  // --------------- STATES ---------------

  // Pagination
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(constants.DEFAULT_PAGE_SIZE);

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const {
    value: contractStatus,
    renderProps: contractStatusFilterRenderProps,
  } = useFilter({
    label: t("filter.status.label"),
    selectionList: contractStatusItems,
    options: {
      searchParamKey: BillingContractSearchParamKey.STATUS,
    },
  });

  // --------------- PARAMETERS ---------------

  const fetchParams = useMemo<Parameters<typeof paymentService.listContracts>>(
    () => [
      workspaceId,
      {
        status: contractStatus,
        page: selectedPage,
        size: pageSize,
        q: searchKeyword,
      },
    ],
    [workspaceId, contractStatus, selectedPage, pageSize, searchKeyword]
  );

  // --------------- METHODS ---------------

  const onChangePage = (page: number) => {
    setSelectedPage(page);
  };

  const onChangePageSize = (size: number) => {
    setPageSize(size);
    setSelectedPage(1);
  };

  // --------------- HOOKS ---------------

  const {
    list: contractList,
    count: contractCount,
    isLoading,
  } = useFetchPaginated(paymentService.listContracts, fetchParams);

  // --------------- RENDER ---------------

  return (
    <>
      <div className="flex justify-between items-start gap-2">
        <div className="flex gap-4 justify-between flex-1">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <FilterDropdownV2 {...contractStatusFilterRenderProps} />
          </div>
          {/* Filters end */}
        </div>

        {/* Searchbox */}
        <SearchboxV2
          value={searchKeyword}
          onChange={(value) => {
            setSearchKeyword(value);
          }}
          placeholder={t("filter.searchbox.placeholder")}
        />
        {/* Searchbox end */}
      </div>

      <TableV2<IPaymentContract>
        data={contractList ?? []}
        getRowKey={(item) => item.product.name.en}
        isLoading={isLoading}
        columns={[
          {
            title: t("header.contractId"),
            render: (item) => (
              <Tooltip title={item.id}>
                <div className="w-full px-2 py-0.5 flex items-center justify-center rounded-full border border-gray-v2-200 bg-gray-v2-50 text-xs font-medium text-gray-v2-700">
                  {item.id.slice(-6)}
                </div>
              </Tooltip>
            ),
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.orderId"),
            render: (item) => (
              <Tooltip title={item.payment_id}>
                <div className="w-full px-2 py-0.5 flex items-center justify-center rounded-full border border-gray-v2-200 bg-gray-v2-50 text-xs font-medium text-gray-v2-700">
                  {item.payment_id.slice(-6)}
                </div>
              </Tooltip>
            ),
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.productService"),
            render: (item) =>
              locale === "vi" ? item.product.name.vi : item.product.name.en,
            gridTemplateValue: "2fr",
          },
          {
            title: t("header.signingMethod"),
            render: (item) =>
              item.contract_type === ContractTypeValue.DIRECT
                ? t("labels.contractType.direct")
                : item.contract_type === ContractTypeValue.ESIGN
                  ? t("labels.contractType.esign")
                  : null,
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.signedDate"),
            render: (item) =>
              item.signed_time
                ? DateTime.fromSeconds(item.signed_time).toFormat("dd/MM/yyyy")
                : "-",
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.status"),
            render: (item) =>
              item.status === ContractStatusValue.NOT_SIGNED ? (
                <Tooltip
                  color={TAILWIND_COLORS["gray-v2-900"]}
                  title={
                    <p className="text-xs text-white font-semibold text-center">
                      {t("contactSupportForUnsigned")}
                      <Link
                        to={`mailto:${constants.CYSTACK_SUPPORT}`}
                        target="_blank"
                        className="no-underline font-semibold text-brand-v2-300 hover:text-brand-v2-400"
                      >
                        {constants.CYSTACK_SUPPORT}
                      </Link>
                    </p>
                  }
                >
                  {/* Antd Tooltip is not smart enough to accept custom React components. */}
                  <span>
                    <CustomBadgeV2ForStatus
                      name={t("labels.contractStatus.notSigned")}
                      icon={
                        <InfoCircle className="h-3 w-3 min-w-3 text-error-v2" />
                      }
                      color={"error"}
                    />
                  </span>
                </Tooltip>
              ) : item.status === ContractStatusValue.SIGNED ? (
                <CustomBadgeV2ForStatus
                  name={t("labels.contractStatus.signed")}
                  icon={
                    <CheckCircle className="h-3 w-3 min-w-3 text-success-v2" />
                  }
                  color={"success"}
                />
              ) : (
                "-"
              ),
            gridTemplateValue: "1fr",
          },
        ]}
        options={{ fitPageHeight: true }}
        pagination={{
          count: contractCount,
          selected: selectedPage,
          onChange: onChangePage,
          size: { value: pageSize, onChange: onChangePageSize },
        }}
      />
    </>
  );
};
